/**
 * ImprovementLogPage — superadmin traceability for every iteration.
 *
 * Renders the parsed PRD.md as a chronological timeline. Each entry
 * surfaces:
 *  • Iteration number, date, category, title, status badge
 *  • Verbatim "User asked" prompt where preserved (highlighted)
 *  • Full markdown body (collapsed by default; click to expand)
 *  • Quick filters: search box + category chips + status chips
 *  • Per-row actions: Publish / Internal improvement / Internal bug fix
 *
 * Source of truth lives at /app/memory/PRD.md; this page is read-only
 * for the markdown body but writes a `status` per iteration into the
 * `changelog_publication_state` Mongo collection.
 */
import { useEffect, useState, useMemo } from "react";
import { Funnel, MagnifyingGlass, Quotes, Clock, FileText, ArrowsClockwise, CheckCircle, Wrench, Bug, ClockClockwise, Envelope } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import AdminPageHeader from "@/components/AdminPageHeader";

const CATEGORIES = ["all", "feature", "fix", "refactor", "docs"];
const STATUS_OPTS = ["all", "pending", "published", "internal_improvement", "internal_bugfix", "hidden"];
const CAT_TONE = {
  feature: "bg-emerald-50 text-emerald-700 border-emerald-200",
  fix: "bg-rose-50 text-rose-700 border-rose-200",
  refactor: "bg-amber-50 text-amber-700 border-amber-200",
  docs: "bg-blue-50 text-blue-700 border-blue-200",
};
const STATUS_TONE = {
  pending: "bg-amber-100 text-amber-900 border-amber-300",
  published: "bg-emerald-100 text-emerald-900 border-emerald-300",
  internal_improvement: "bg-violet-100 text-violet-900 border-violet-300",
  internal_bugfix: "bg-sky-100 text-sky-900 border-sky-300",
  hidden: "bg-slate-100 text-slate-500 border-slate-200",
};
const STATUS_LABEL = {
  pending: "Pending review",
  published: "Published",
  internal_improvement: "Internal improvement",
  internal_bugfix: "Internal bug fix",
  hidden: "Hidden",
};

export default function ImprovementLogPage({ user, onLogout, onLogoutAll }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [withPrompt, setWithPrompt] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expanded, setExpanded] = useState({});
  const [busyIter, setBusyIter] = useState("");
  const [sourceMtime, setSourceMtime] = useState(null);
  const [subscriberCount, setSubscriberCount] = useState(null);
  // iter149d — pending user-submitted suggestions live on a separate
  // page (`/admin/requested-changes`). Surface a banner on this page
  // so admins don't go hunting.
  const [pendingRequests, setPendingRequests] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/suggestions?status=pending")
      .then((r) => { if (!cancelled) setPendingRequests(r.data?.counts?.pending || 0); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (q.trim()) params.q = q.trim();
      if (category && category !== "all") params.category = category;
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      const r = await api.get("/admin/improvement-log", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setWithPrompt(r.data.with_prompt || 0);
      setStatusCounts(r.data.status_counts || {});
      setSourceMtime(r.data.source_mtime || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load improvement log");
    } finally {
      setLoading(false);
    }
  };

  const refreshSubs = () => {
    api.get("/admin/improvement-log/subscribers")
      .then((r) => setSubscriberCount(r.data?.total ?? null))
      .catch(() => setSubscriberCount(null));
  };

  const [digestBusy, setDigestBusy] = useState(false);

  const runDigest = async (dry_run = true) => {
    if (digestBusy) return;
    if (!dry_run && !window.confirm(
      `Send the digest to ${subscriberCount ?? "all"} subscriber(s)? Each gets only the iterations published since their last digest.`
    )) return;
    setDigestBusy(true);
    try {
      const r = await api.post("/admin/improvement-log/send-digest", { dry_run });
      const sent = r.data?.sent || [];
      const skipped = r.data?.skipped || [];
      if (dry_run) {
        toast.info(`Dry run: ${sent.length} would receive · ${skipped.length} would be skipped`);
      } else {
        const mocked = sent.filter((s) => s.mocked).length;
        toast.success(
          `Digest sent: ${sent.length} delivered${mocked ? ` (${mocked} mocked — SMTP not set)` : ""} · ${skipped.length} skipped`
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Digest failed");
    } finally {
      setDigestBusy(false);
    }
  };

  // Initial load + refetch on filter change.
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [category, statusFilter]);
  useEffect(() => { refreshSubs(); }, []);

  // Search debounce.
  useEffect(() => {
    const id = setTimeout(refresh, 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line
  }, [q]);

  const setStatus = async (iter_id, newStatus) => {
    if (busyIter) return;
    setBusyIter(iter_id);
    try {
      await api.put(`/admin/improvement-log/${encodeURIComponent(iter_id)}/status`, { status: newStatus });
      toast.success(`iter ${iter_id} → ${STATUS_LABEL[newStatus] || newStatus}`);
      // Optimistic patch to avoid a full reload flicker.
      setItems((prev) => prev.map((e) => e.iter_id === iter_id
        ? { ...e, status: newStatus, reviewed_by: "you", reviewed_at: new Date().toISOString() }
        : e));
      // Pull fresh counts.
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status change failed");
    } finally {
      setBusyIter("");
    }
  };

  const stats = useMemo(() => {
    const byCat = {};
    items.forEach((e) => { byCat[e.category] = (byCat[e.category] || 0) + 1; });
    return byCat;
  }, [items]);

  return (
    <div className="min-h-screen bg-slate-50 font-['Lato'] text-slate-900" data-testid="improvement-log-page">
      <AdminPageHeader
        sticky
        maxWidth="max-w-5xl"
        testId="improvement-log-back"
        icon={<Clock size={20} weight="fill" className="text-blue-700" />}
        title="Improvement Log"
        subtitle="Review every iteration · approve customer-facing posts before they go live."
        user={user}
        onLogout={onLogout}
        onLogoutAll={onLogoutAll}
      />

      <main className="max-w-5xl mx-auto p-6 space-y-5">

        {/* iter149d — Banner pointing at the Requested Changes triage
            page. The Improvement Log is the AI-curated history; new
            user submissions land in /admin/requested-changes and were
            historically easy to miss. */}
        {pendingRequests > 0 && (
          <a href="/admin/requested-changes"
             data-testid="improvement-log-requested-changes-banner"
             className="block bg-amber-50 border-l-4 border-amber-400 rounded-md p-4 hover:bg-amber-100 transition">
            <div className="flex items-center gap-3">
              <Clock size={18} weight="fill" className="text-amber-600 shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-bold text-amber-900">
                  {pendingRequests} new user-submitted change request{pendingRequests === 1 ? "" : "s"} waiting in triage
                </div>
                <div className="text-xs text-amber-800 mt-0.5">
                  These come from the <b>Suggest a change</b> button. Open the queue to review and approve them →
                </div>
              </div>
            </div>
          </a>
        )}

        {/* Status summary card */}
        <section className="bg-white border border-slate-200 rounded-md p-4" data-testid="improvement-log-status-summary">
          <div className="flex flex-wrap items-center gap-3">
            <StatusPill tone="amber" count={statusCounts.pending} label="Pending review" testId="status-summary-pending" />
            <StatusPill tone="emerald" count={statusCounts.published} label="Published" testId="status-summary-published" />
            <StatusPill tone="violet" count={statusCounts.internal_improvement} label="Internal improvement" testId="status-summary-internal-improvement" />
            <StatusPill tone="sky" count={statusCounts.internal_bugfix} label="Internal bug fix" testId="status-summary-internal-bugfix" />
            <StatusPill tone="slate" count={statusCounts.hidden} label="Hidden (no public summary)" testId="status-summary-hidden" />
            <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] text-slate-600">
              <Envelope size={12} weight="bold" />
              <a href="/whats-new" target="_blank" rel="noopener noreferrer" className="hover:underline">Public page</a>
              {subscriberCount !== null && (
                <span className="text-slate-400">· {subscriberCount} subscriber{subscriberCount === 1 ? "" : "s"}</span>
              )}
              {subscriberCount > 0 && (
                <>
                  <button
                    type="button"
                    data-testid="digest-dry-run"
                    onClick={() => runDigest(true)}
                    disabled={digestBusy}
                    title="Preview who would receive the digest, without sending"
                    className="ml-2 px-2 py-0.5 border border-slate-300 rounded-sm text-[10px] font-bold uppercase tracking-wider hover:bg-slate-50 disabled:opacity-40">
                    Preview digest
                  </button>
                  <button
                    type="button"
                    data-testid="digest-send"
                    onClick={() => runDigest(false)}
                    disabled={digestBusy}
                    title="Send the digest to every active subscriber in their saved language"
                    className="px-2 py-0.5 border border-blue-300 bg-blue-50 text-blue-800 rounded-sm text-[10px] font-bold uppercase tracking-wider hover:bg-blue-100 disabled:opacity-40">
                    {digestBusy ? "Sending…" : "Send digest now"}
                  </button>
                </>
              )}
            </span>
          </div>
        </section>

        {/* Filters */}
        <section className="bg-white border border-slate-200 rounded-md p-4 space-y-3" data-testid="improvement-log-filters">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <MagnifyingGlass size={14} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search title, summary, or verbatim prompt…"
                data-testid="improvement-log-search"
                className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-sm focus:border-blue-500 focus:outline-none"
              />
            </div>
            <button
              onClick={refresh}
              data-testid="improvement-log-refresh"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider border border-slate-200 rounded-sm hover:bg-slate-50">
              <ArrowsClockwise size={12} weight="bold" />
              Refresh
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Funnel size={12} weight="bold" className="text-slate-400" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mr-1">Category:</span>
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`improvement-log-cat-${c}`}
                className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border ${
                  category === c
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                }`}>
                {c} {c !== "all" && stats[c] ? `(${stats[c]})` : ""}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mr-1">Status:</span>
            {STATUS_OPTS.map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                data-testid={`improvement-log-status-${s}`}
                className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border ${
                  statusFilter === s
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                }`}>
                {STATUS_LABEL[s] || s}
              </button>
            ))}
            <span className="ml-auto text-[11px] text-slate-500">
              {loading ? "Loading…" : `${total} iteration${total === 1 ? "" : "s"} · ${withPrompt} with verbatim prompt`}
            </span>
          </div>
          {sourceMtime && !loading && (
            <p className="text-[10px] text-slate-400 italic flex items-center gap-1">
              <FileText size={10} weight="bold" />
              Source: <span className="font-mono">/app/memory/PRD.md</span> ·
              last updated {new Date(sourceMtime * 1000).toLocaleString()}
            </p>
          )}
        </section>

        {/* Timeline */}
        {!loading && items.length === 0 && (
          <div className="text-center py-16 text-slate-500 text-sm" data-testid="improvement-log-empty">
            No iterations match the current filter.
          </div>
        )}

        <ol className="relative border-l-2 border-slate-200 ml-3 space-y-4" data-testid="improvement-log-list">
          {items.map((entry) => {
            const open = !!expanded[entry.iter_id];
            const tone = CAT_TONE[entry.category] || "bg-slate-50 text-slate-700 border-slate-200";
            const stTone = STATUS_TONE[entry.status] || STATUS_TONE.hidden;
            const canReview = entry.status !== "hidden" || entry.customer_summary;
            const busy = busyIter === entry.iter_id;
            return (
              <li key={entry.iter_id} className="ml-4" data-testid={`improvement-log-entry-${entry.iter_id}`}>
                <span className="absolute -left-[7px] mt-1.5 w-3 h-3 rounded-full bg-white border-2 border-slate-300" />

                <div className="bg-white border border-slate-200 rounded-md hover:shadow-sm transition-shadow">
                  <button
                    onClick={() => setExpanded((s) => ({ ...s, [entry.iter_id]: !open }))}
                    className="w-full text-left p-3 flex flex-wrap items-baseline gap-2"
                    data-testid={`improvement-log-toggle-${entry.iter_id}`}>
                    <span className="text-[10px] font-mono font-bold text-slate-500 uppercase">{entry.date}</span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${tone}`}>
                      {entry.category}
                    </span>
                    <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-sm bg-slate-900 text-white">
                      iter {entry.iter_id}
                    </span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${stTone}`}
                          data-testid={`improvement-log-status-badge-${entry.iter_id}`}>
                      {STATUS_LABEL[entry.status] || entry.status}
                    </span>
                    <span className="text-sm font-bold text-slate-900 flex-1 min-w-0">{entry.title}</span>
                    <span className="text-[10px] text-slate-400">{open ? "▼" : "▶"}</span>
                  </button>

                  {/* Customer summary (if any) */}
                  {entry.customer_summary && (
                    <div className="mx-3 mb-2 bg-blue-50 border-l-4 border-blue-400 px-3 py-2 rounded-sm">
                      <div className="text-[9px] font-bold uppercase tracking-wider text-blue-900 mb-0.5">Customer summary</div>
                      <div className="text-xs text-slate-800 leading-relaxed">{entry.customer_summary_override || entry.customer_summary}</div>
                    </div>
                  )}

                  {/* Verbatim prompt */}
                  {entry.user_prompt ? (
                    <div className="mx-3 mb-2 bg-amber-50 border-l-4 border-amber-400 px-3 py-2 rounded-sm"
                         data-testid={`improvement-log-prompt-${entry.iter_id}`}>
                      <div className="flex items-start gap-2">
                        <Quotes size={12} weight="fill" className="text-amber-700 mt-0.5 shrink-0" />
                        <div className="min-w-0">
                          <div className="text-[9px] font-bold uppercase tracking-wider text-amber-800 mb-0.5">User asked</div>
                          <div className="text-xs text-slate-800 italic leading-relaxed whitespace-pre-wrap break-words">
                            {entry.user_prompt}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-[10px] italic text-slate-400 px-3 py-1"
                         data-testid={`improvement-log-no-prompt-${entry.iter_id}`}>
                      No verbatim prompt preserved for this iteration —
                      recovered from the change description below.
                    </div>
                  )}

                  {/* Action row */}
                  {canReview && (
                    <div className="border-t border-slate-100 px-3 py-2 flex flex-wrap items-center gap-2" data-testid={`improvement-log-actions-${entry.iter_id}`}>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mr-1">Move to:</span>
                      <ActionBtn busy={busy} onClick={() => setStatus(entry.iter_id, "published")} active={entry.status === "published"} testId={`set-published-${entry.iter_id}`} icon={<CheckCircle size={11} weight="fill" />} tone="emerald">Published</ActionBtn>
                      <ActionBtn busy={busy} onClick={() => setStatus(entry.iter_id, "internal_improvement")} active={entry.status === "internal_improvement"} testId={`set-improvement-${entry.iter_id}`} icon={<Wrench size={11} weight="fill" />} tone="violet">Internal improvement</ActionBtn>
                      <ActionBtn busy={busy} onClick={() => setStatus(entry.iter_id, "internal_bugfix")} active={entry.status === "internal_bugfix"} testId={`set-bugfix-${entry.iter_id}`} icon={<Bug size={11} weight="fill" />} tone="sky">Internal bug fix</ActionBtn>
                      {entry.status !== "pending" && entry.status !== "hidden" && (
                        <ActionBtn busy={busy} onClick={() => setStatus(entry.iter_id, "pending")} active={false} testId={`set-pending-${entry.iter_id}`} icon={<ClockClockwise size={11} weight="fill" />} tone="amber">Reset to pending</ActionBtn>
                      )}
                      {entry.reviewed_by && entry.reviewed_at && (
                        <span className="ml-auto text-[10px] text-slate-400">
                          {entry.reviewed_by === "system-seed"
                            ? "auto-approved on launch"
                            : `by ${entry.reviewed_by} · ${new Date(entry.reviewed_at).toLocaleDateString()}`}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Body — collapsed unless toggled */}
                  {open && (
                    <pre
                      data-testid={`improvement-log-body-${entry.iter_id}`}
                      className="px-3 pb-3 text-[11px] leading-relaxed text-slate-700 whitespace-pre-wrap break-words font-sans border-t border-slate-100 pt-3"
                    >{entry.body_markdown}</pre>
                  )}
                </div>
              </li>
            );
          })}
        </ol>

      </main>
    </div>
  );
}

function StatusPill({ count, label, tone, testId }) {
  const map = {
    amber: "bg-amber-50 text-amber-900 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-900 border-emerald-200",
    violet: "bg-violet-50 text-violet-900 border-violet-200",
    sky: "bg-sky-50 text-sky-900 border-sky-200",
    slate: "bg-slate-50 text-slate-600 border-slate-200",
  };
  return (
    <div data-testid={testId} className={`flex items-baseline gap-1.5 px-2.5 py-1.5 rounded-sm border text-xs ${map[tone] || map.slate}`}>
      <span className="font-bold text-base font-mono">{count ?? 0}</span>
      <span className="text-[10px] uppercase tracking-wider font-bold">{label}</span>
    </div>
  );
}

function ActionBtn({ children, onClick, active, busy, icon, tone, testId }) {
  const map = {
    emerald: "border-emerald-300 text-emerald-700 hover:bg-emerald-50",
    violet: "border-violet-300 text-violet-700 hover:bg-violet-50",
    sky: "border-sky-300 text-sky-700 hover:bg-sky-50",
    amber: "border-amber-300 text-amber-700 hover:bg-amber-50",
  };
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={busy || active}
      className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border ${map[tone] || ""} ${active ? "ring-2 ring-offset-1 ring-current cursor-default" : ""} disabled:opacity-50`}>
      {icon}
      {children}
    </button>
  );
}
